<template>
  <div class="detail-wrapper">
    <div v-for="(item, idx) in store.details" :key="'large-'+item.id+'-'+idx">
      <transition name="large-image-fade" mode="out-in">
        <div v-if="item.showLarge" class="large-image-overlay-fixed" @click="closeLargeImage(item)">
          <div class="large-image-loader" v-if="isLargeImageLoading"></div>
          <img 
            :src="item.element.image_paths[0]" 
            :alt="item.element.name" 
            class="large-image-fixed"
            :class="{ loaded: !isLargeImageLoading }"
            @load="isLargeImageLoading = false"
            @click.stop
          />
          <button class="large-close-fixed" @click.stop="closeLargeImage(item)">×</button>
        </div>
      </transition>
    </div>

    <transition-group name="panel-fade" tag="div" class="detail-panel-container">
      <div
        v-for="(item, index) in store.details"
        :key="item.id + '-' + index"
        class="detail-panel-overlay"
        @click.self="closePanel(index)"
      >
        <div
          :ref="el => panels[index] = el"
          class="detail-card"
          :style="getPanelStyle(item)"
          @mousedown.prevent="startDrag($event, index)"
        >
          <div class="card-header" :style="getHeaderGradient(item)">
            <div class="image-container" @click.stop="openLargeImage(item)">
              <div class="image-loader" v-if="!imageLoadedStatus[item.id]"></div>
              <img 
                :src="item.element.image_paths[0]" 
                :alt="item.element.name" 
                class="header-image"
                :class="{ loaded: imageLoadedStatus[item.id] }"
                @load="() => imageLoadedStatus[item.id] = true"
              />
            </div>
          </div>

          <div class="card-body">
            <button class="close-button" @click.stop="closePanel(index)">×</button>
            <div class="title-section">
              <div class="title">{{ item.element.name }}</div>
            </div>
            <div class="properties-section">
              <div class="property-item color-property">
                <span class="property-label">COLOR</span>
                <div class="color-value-container">
                  <div class="swatch" :style="{ backgroundColor: item.color.hex }"></div>
                  <span class="property-value">{{ item.color.hex.toUpperCase() }}</span>
                </div>
              </div>
              <template v-for="prop in getProperties(item)" :key="prop.label">
                <div class="property-item">
                  <span class="property-label">{{ prop.label }}</span>
                  <span class="property-value">{{ prop.value }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onUnmounted } from 'vue';
import { useMainStore } from '@/stores/mainStore';
const store = useMainStore();

const imageLoadedStatus = ref({});
const isLargeImageLoading = ref(true);

// --- JS动画拖拽相关状态 ---
const positions = ref({});
const animationFrameId = ref(null);
// 您可以自由调整这个值，值越小，滑动越“长”
const easingFactor = 0.05; 

// --- 新增：用于区分用户交互和动画执行的状态 ---
const drag = ref({ 
  isInteracting: false, // 标记用户是否正按着鼠标拖动
  index: null, 
  offsetX: 0, 
  offsetY: 0 
});


const getHeaderGradient = (item) => {
  return { background: `linear-gradient(160deg, ${item.color.hex}, #4a4a4a)` };
};

function openLargeImage(item) {
  isLargeImageLoading.value = true;
  item.showLarge = true;
}
function closeLargeImage(item) {
  item.showLarge = false;
}

const getProperties = (item) => {
    const props = [];
    const el = item.element;
    if (!el) return [];
    if (el.full_category?.name) props.push({ label: '类别', value: el.full_category.name });
    if (el.full_location?.altitude) props.push({ label: '海拔', value: `${el.full_location.altitude}m` });
    if (el.full_season?.name) props.push({ label: '季节', value: el.full_season.name || '四季' });
    if (el.full_location?.latitude && el.full_location?.longitude) {
        const lat = parseFloat(el.full_location.latitude).toFixed(4);
        const lon = parseFloat(el.full_location.longitude).toFixed(4);
        props.push({ label: '经纬度', value: `${lat}, ${lon}` });
    }
    return props;
};

const panels = ref([]);

function getPanelStyle(item) {
  if (item.initial) {
    return { left: item.x + 'px', top: item.y + 'px', transform: 'translate(-50%, -50%)' };
  }
  
  const pos = positions.value[item.id];
  const x = pos ? pos.currentX : item.x;
  const y = pos ? pos.currentY : item.y;

  return { 
    transform: `translate(${x}px, ${y}px)`,
    left: '0px', 
    top: '0px'
  };
}

function closePanel(index) {
  const item = store.details[index];
  if (item) {
    delete imageLoadedStatus.value[item.id];
    delete positions.value[item.id];
  }
  store.removeDetail(index);
}

// --- 核心修改：动画循环逻辑 ---
function runAnimation() {
  const index = drag.value.index;
  // 如果没有正在处理的面板，则直接停止
  if (index === null || !store.details[index]) {
    stopAnimation();
    return;
  }
  
  const item = store.details[index];
  const pos = positions.value[item.id];
  const targetX = item.x;
  const targetY = item.y;

  const dx = targetX - pos.currentX;
  const dy = targetY - pos.currentY;

  // 停止条件：如果用户已不再交互，并且卡片已经非常接近目标点
  if (!drag.value.isInteracting && Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
    pos.currentX = targetX; // 精准吸附到最终位置
    pos.currentY = targetY;
    stopAnimation(); // 停止动画循环
  } else {
    // 持续向目标位置缓动
    pos.currentX += dx * easingFactor;
    pos.currentY += dy * easingFactor;
    animationFrameId.value = requestAnimationFrame(runAnimation);
  }
}

function stopAnimation() {
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value);
    animationFrameId.value = null;
  }
}

function startDrag(event, index) {
  if (event.target.closest('button, .image-container')) return;
  
  const item = store.details[index];
  
  if (item.initial) {
    const rect = panels.value[index].getBoundingClientRect();
    item.x = rect.left;
    item.y = rect.top;
    item.initial = false;
  }

  if (!positions.value[item.id]) {
    positions.value[item.id] = { currentX: item.x, currentY: item.y };
  }

  // 设置拖拽交互状态
  drag.value = {
    isInteracting: true,
    index,
    offsetX: event.clientX - positions.value[item.id].currentX, // 基于当前渲染位置计算偏移
    offsetY: event.clientY - positions.value[item.id].currentY,
  };
  
  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
  
  // 如果动画没在运行，则启动它
  if (!animationFrameId.value) {
    runAnimation();
  }
}

function onDrag(event) {
  if (!drag.value.isInteracting) return;
  
  const { index, offsetX, offsetY } = drag.value;
  // 拖动时，持续更新目标位置
  store.details[index].x = event.clientX - offsetX;
  store.details[index].y = event.clientY - offsetY;
}

function stopDrag() {
  // 仅仅是停止“用户交互”，动画循环会自己判断是否需要继续
  drag.value.isInteracting = false;
  
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
}

onUnmounted(() => {
  // 组件卸载时，彻底停止交互和动画
  stopDrag();
  stopAnimation();
});
</script>


<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@500;700&family=Roboto+Mono:wght@400;500&display=swap');

.detail-panel-overlay {
  position: fixed; top: 0; left: 0;
  width: 100%; height: 100%;
  background: transparent; z-index: 200; pointer-events: all;
}

.detail-card {
  position: fixed;
  width: 260px;
  background: #ffffff;
  box-shadow: 0 12px 35px rgba(0,0,0,0.12);
  cursor: grab;
  font-family: 'Inter', sans-serif;
  clip-path: polygon(30px 0, 100% 0, 100% 100%, 0 100%, 0 30px);
  /* 移除：不再需要CSS transition来做拖拽动画 */
  /* transition: transform 100ms ease-out; */
}
.detail-card:active { cursor: grabbing; }

.card-header {
  position: relative;
  height: 200px;
}
.image-container {
  width: 100%; height: 100%;
  cursor: pointer; overflow: hidden;
  position: relative;
}

.image-loader {
  position: absolute;
  top: 50%; left: 50%;
  width: 24px;
  height: 24px;
  margin: -12px 0 0 -12px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.header-image {
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease, opacity 0.6s ease;
  opacity: 0;
}
.header-image.loaded {
  opacity: 1;
}
.image-container:hover .header-image { transform: scale(1.08); }

.card-body {
  padding: 20px;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
  border-bottom: 1px solid #f5f5f5;
  padding-bottom: 12px;
}

.title {
  margin: 0; padding: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #222;
  text-transform: uppercase;
  line-height: 1;
}

.properties-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.property-item {
  display: flex; flex-direction: column;
  align-items: flex-start; gap: 4px;
}

.property-label {
  color: #c0c0c0;
  font-family: 'Roboto Mono', monospace;
  font-size: 9px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.property-value {
  color: #333;
  font-weight: 500;
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
}

.color-property .color-value-container {
  display: flex; align-items: center; gap: 6px;
}
.swatch {
  width: 12px; height: 12px;
  border-radius: 3px; border: 1px solid #eee;
  flex-shrink: 0;
}

.close-button {
  position: absolute; top: 6px; right: 6px;
  width: 24px; height: 24px; border: none;
  background: transparent; color: #d0d0d0;
  border-radius: 50%; font-size: 18px; line-height: 24px;
  text-align: center; cursor: pointer;
  transition: all .2s ease; z-index: 10;
}
.close-button:hover {
  transform: rotate(90deg) scale(1.1);
  background: #f5f5f5; color: #555;
}

/* --- 大图预览和过渡动画样式 --- */
.large-image-overlay-fixed {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.8);
  backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  z-index: 999; cursor: zoom-out;
}

.large-image-loader {
  position: absolute;
  top: 50%; left: 50%;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 1;
}

.large-image-fixed {
  max-width: 90vw; max-height: 90vh;
  opacity: 0;
  transition: opacity 0.4s ease-in-out;
}
.large-image-fixed.loaded {
  opacity: 1;
}

.large-close-fixed {
  position: absolute; top: 20px; right: 20px;
  background: rgba(255,255,255,0.8); border: none; font-size: 1.5rem;
  width: 40px; height: 40px; border-radius: 50%; cursor: pointer;
  display: flex; align-items: center; justify-content: center; line-height: 1;
  z-index: 2;
}

.large-image-fade-enter-active,
.large-image-fade-leave-active {
  transition: opacity 0.3s ease;
}
.large-image-fade-enter-active .large-image-fixed,
.large-image-fade-leave-active .large-image-fixed {
  transition: transform 0.3s ease, opacity 0.4s ease-in-out;
}

.large-image-fade-enter-from,
.large-image-fade-leave-to {
  opacity: 0;
}
.large-image-fade-enter-from .large-image-fixed,
.large-image-fade-leave-to .large-image-fixed {
  transform: scale(0.95);
}


.panel-fade-enter-active, .panel-fade-leave-active {
  transition: opacity .3s ease, transform .3s ease;
}
.panel-fade-enter-from, .panel-fade-leave-to {
  opacity: 0; transform: scale(0.95) !important;
}
</style>